interface LineKeyData<T> {
  key: LineKey;
  value: T;
}

export class LineKeyMap<T> {
  items: LineKeyData<T>[];
  constructor() {
    this.items = [];
  }

  some(predicate: (value: T) => boolean): boolean {
    return this.items.some((item) => predicate(item.value));
  }

  get(key: LineKey): T | null {
    return this.items.find((item) => item.key.isEqual(key))?.value ?? null;
  }

  set(key: LineKey, value: T) {
    const data = { key, value };
    const existingIndex = this.items.findIndex((item) => item.key.isEqual(key));
    if (existingIndex >= 0) {
      this.items[existingIndex] = data;
    } else {
      this.items.push(data);
    }
  }

  delete(key: LineKey) {
    const index = this.items.findIndex((item) => item.key.isEqual(key));
    if (index >= 0) {
      this.items.splice(index, 1);
    }
  }
}

export class LineKey {
  lineNumber: string;
  lineNumberPosition: string;
  warehouse: string;
  jointDeliveryCode: string;
  transactionReason: string;
  itemNumber: string;
  extra: string;
  useOnlyLineNumbers: boolean;
  constructor(
    line: {
      lineNumber?: string;
      lineNumberPosition?: string;
      warehouse?: string;
      jointDeliveryCode?: string;
      transactionReason?: string;
      itemNumber?: string;
    },
    extra?: string,
    useOnlyLineNumbers: boolean = false
  ) {
    if (!line.itemNumber && !useOnlyLineNumbers) {
      console.groupCollapsed('LineKey created without itemNumber');
      console.log('line:', line);
      console.trace();
      console.groupEnd();
    }

    this.lineNumber = line.lineNumber ?? '0';
    this.lineNumberPosition = line.lineNumberPosition ?? '0';
    this.warehouse = line.warehouse ?? '';
    this.jointDeliveryCode = line.jointDeliveryCode ?? '';
    this.transactionReason = line.transactionReason ?? '';
    this.itemNumber = line.itemNumber ?? '';
    this.extra = extra ?? '';
    this.useOnlyLineNumbers = useOnlyLineNumbers;
  }

  shouldIgnoreLineNumbers(key: LineKey) {
    // Ignore line numbers for pallet and freight rows, this check should be based on userDefinedField1Item
    // but that requires article data
    return (
      !this.useOnlyLineNumbers &&
      [this, key].some(
        (k) =>
          k.transactionReason &&
          (k.transactionReason.startsWith('BF') || ['FRA', 'EMB', 'PDK'].includes(k.transactionReason))
      )
    );
  }

  isEqual(key: LineKey, ...additional_checks: ((a: LineKey, b: LineKey) => boolean)[]): boolean {
    const checks = [
      (a: LineKey, b: LineKey) => {
        if (a.shouldIgnoreLineNumbers(b)) {
          return (
            a.warehouse === b.warehouse &&
            a.jointDeliveryCode === b.jointDeliveryCode &&
            a.itemNumber === b.itemNumber &&
            // Can't match on transactionReason bc M3 rows can have different xactReasons than the freightResponse
            a.extra === b.extra
          );
        }

        return (
          a.lineNumber === b.lineNumber &&
          a.lineNumberPosition === b.lineNumberPosition &&
          a.itemNumber === b.itemNumber &&
          a.extra === b.extra
        );
      },
      ...additional_checks,
    ];

    for (const c of checks) {
      if (!c(this, key)) return false;
    }

    return true;
  }

  toString(): string {
    let parts: string[] = [];
    for (const part of [
      this.lineNumber,
      this.lineNumberPosition,
      this.warehouse,
      this.jointDeliveryCode,
      this.transactionReason,
      this.itemNumber,
      this.extra,
    ]) {
      parts.push(part ?? '');
    }
    return parts.join('-');
  }

  static fromString(key: string): LineKey {
    const [lineNumber, lineNumberPosition, warehouse, jointDeliveryCode, transactionReason, itemNumber, extra] =
      key.split('-');
    return new LineKey(
      { lineNumber, lineNumberPosition, warehouse, jointDeliveryCode, transactionReason, itemNumber },
      extra
    );
  }
}
